import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, FolderOpen, ChevronRight, ChevronLeft, FileText } from "lucide-react";
import MandanteLogo from "@/components/MandanteLogo";

/** Lista de mandantes para iniciar flujo de acreditación. */
export function AcreditacionList() {
  const nav = useNavigate();
  const [mandantes, setMandantes] = useState([]);

  useEffect(() => { api.get("/mandantes").then((r) => setMandantes(r.data)); }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><FolderOpen size={28} className="text-emerald-600" /> Acreditación</h1>
        <p className="text-slate-500 mt-1">Carga los documentos reales (PDF) de cada contrato y de cada trabajador. La configuración está en <Link to="/mandantes" className="text-indigo-600 hover:underline">Mandantes</Link>.</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {mandantes.length === 0 ? (
          <p className="p-10 text-center text-sm text-slate-400 italic">Sin mandantes configurados.</p>
        ) : (
          <table data-testid="acred-mandantes-table" className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3">Mandante</th>
                <th className="px-5 py-3 text-right">Contratos</th>
                <th className="px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {mandantes.map((m) => (
                <tr key={m.id} data-testid={`acred-mandante-row-${m.id}`} className="border-t border-slate-50 hover:bg-emerald-50/40 cursor-pointer" onClick={() => nav(`/acreditacion/${m.id}`)}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <MandanteLogo mandante={m} size="sm" />
                      <span className="font-semibold text-slate-800">{m.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-slate-700">{(m.contratos || []).length}</td>
                  <td className="px-5 py-3 text-right"><ChevronRight size={16} className="text-slate-300" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Lista de contratos de un mandante en modo Acreditación. */
export function AcreditacionMandante() {
  const { id } = useParams();
  const nav = useNavigate();
  const [mandante, setMandante] = useState(null);
  const [workers, setWorkers] = useState([]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const s = String(iso).slice(0, 10);
    const [y, m, d] = s.split("-");
    return y && m && d ? `${d}-${m}-${y}` : s;
  };

  useEffect(() => {
    api.get("/mandantes").then((r) => {
      const m = r.data.find((x) => x.id === id);
      if (!m) { toast.error("Mandante no encontrado"); nav("/acreditacion"); return; }
      setMandante(m);
    });
    api.get("/workers").then((r) => setWorkers(r.data));
  }, [id, nav]);

  const countContrato = (c) => workers.filter((w) => (w.mandante || w.company) === mandante?.name && w.contrato === c).length;

  if (!mandante) return <div className="text-sm text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
        <Link to="/acreditacion" className="hover:text-emerald-600 inline-flex items-center gap-1"><ChevronLeft size={14} /> Acreditación</Link>
        <ChevronRight size={14} />
        <span className="text-slate-700 font-medium">{mandante.name}</span>
      </nav>
      <div className="flex items-center gap-3">
        <MandanteLogo mandante={mandante} size="lg" />
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-bold text-slate-900">{mandante.name}</h1>
          <p className="text-slate-500 text-sm mt-1">Selecciona un contrato para cargar documentos.</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {(mandante.contratos || []).length === 0 ? (
          <p className="p-10 text-center text-sm text-slate-400 italic">Sin contratos configurados.</p>
        ) : (
          <table data-testid="acred-contratos-table" className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3 whitespace-nowrap">N° Contrato</th>
                <th className="px-5 py-3">Glosa</th>
                <th className="px-5 py-3 whitespace-nowrap">Inicio</th>
                <th className="px-5 py-3 whitespace-nowrap">Término</th>
                <th className="px-5 py-3 text-right">Trab.</th>
                <th className="px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {(mandante.contratos || []).map((c) => (
                <tr key={c.id} data-testid={`acred-contrato-row-${c.id}`} className="border-t border-slate-50 hover:bg-emerald-50/40 cursor-pointer" onClick={() => nav(`/acreditacion/${id}/contratos/${c.id}`)}>
                  <td className="px-5 py-3 font-mono text-[11px] text-slate-700">{c.numero_contrato || <span className="text-slate-300">—</span>}</td>
                  <td className="px-5 py-3 font-semibold text-slate-800 flex items-center gap-2"><FileText size={14} className="text-slate-400" /> {c.name}</td>
                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{formatDate(c.fecha_inicio)}</td>
                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{formatDate(c.fecha_termino)}</td>
                  <td className="px-5 py-3 text-right"><span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{countContrato(c.name)}</span></td>
                  <td className="px-5 py-3 text-right"><ChevronRight size={16} className="text-slate-300" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
